import React, { useEffect, useRef, useState } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5percent from '@amcharts/amcharts5/percent';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';

export interface PieChartData {
  category: string;   // label shown on legend: e.g. "Good", "Bad"
  value: number;
  color?: string;
}

export interface PieChartProps {
  title: string;
  data: PieChartData[];
  totalLabel?: string;
  totalValue?: number;
  width?: number;
  height?: number;
  className?: string;
  // single argument: filter value or null (reset)
  onSliceClick?: (filterValue: string | null) => void;

  // which category should be visually active for this chart
  activeCategory?: string | null;

  // kept for compatibility, not used in layout logic now
  variant?: 'desktop' | 'mobile';
}

const PieChart: React.FC<PieChartProps> = ({
  title,
  data,
  totalLabel = 'Total',
  totalValue,
  width,
  height,
  className = '',
  onSliceClick,
  activeCategory = null,
  variant = 'desktop'
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const rootRef = useRef<am5.Root | null>(null);
  const seriesRef = useRef<am5percent.PieSeries | null>(null);
  const [defaultSize] = useState({ width: 320, height: 260 });

  // keep latest active category (for slice click toggle)
  const activeCategoryPropRef = useRef<string | null>(null);
  useEffect(() => {
    activeCategoryPropRef.current = activeCategory ?? null;
  }, [activeCategory]);

  // ---------------- INIT — run ONLY ONCE ----------------
  useEffect(() => {
    if (!chartRef.current) return;
    if (rootRef.current) return; // Chart already initialized

    am5.addLicense("AM5C137048159");

    const root = am5.Root.new(chartRef.current);
    root.setThemes([am5themes_Animated.new(root)]);
    rootRef.current = root;

    // Main container
    const container = root.container.children.push(
      am5.Container.new(root, {
        layout: root.verticalLayout,
        width: am5.percent(100),
        height: am5.percent(100)
      })
    );

    const chartWrapper = container.children.push(
      am5.Container.new(root, {
        layout: root.verticalLayout,
        width: am5.percent(100),
        height: am5.percent(100),
        centerX: am5.percent(50),
        centerY: am5.percent(50),
        x: am5.percent(50),
        y: am5.percent(50)
      })
    );

    const pieChart = chartWrapper.children.push(
      am5percent.PieChart.new(root, {
        layout: root.verticalLayout,
        innerRadius: am5.percent(60),
        width: am5.percent(100),
        height: am5.percent(100),
        centerX: am5.percent(50),
        centerY: am5.percent(50),
        x: am5.percent(50),
        y: am5.percent(50)
      })
    );

    const series = pieChart.series.push(
      am5percent.PieSeries.new(root, {
        valueField: "value",
        categoryField: "category",
        alignLabels: false
      })
    );

    seriesRef.current = series;

    // Slice styling
    series.slices.template.setAll({
      stroke: am5.color(0xffffff),
      strokeWidth: 2,
      cursorOverStyle: "pointer"
    });

    // Labels
    series.labels.template.setAll({
      textType: "circular",
      centerX: 0,
      centerY: 0,
      text: "{value}",
      fontSize: 14,
      fill: am5.color("#ffffff"),
      fontWeight: "bold"
    });

    // Slice click
    series.slices.template.events.on("click", (ev) => {
      if (!onSliceClick) return;

      const di = ev.target.dataItem;
      if (!di) return;

      const ctx = di.dataContext as PieChartData;
      const category = ctx.category;
      const current = activeCategoryPropRef.current;

      if (current === category) onSliceClick(null);
      else onSliceClick(category);
    });

    // Color adapter
    series.slices.template.adapters.add("fill", (fill, target) => {
      const ctx = target.dataItem?.dataContext as PieChartData;
      return ctx?.color ? am5.color(ctx.color) : fill;
    });

    series.appear(600, 50);

    return () => {
      root.dispose();
      rootRef.current = null;
      seriesRef.current = null;
    };
  }, []); // EMPTY DEPS → RUN ONCE ONLY


  // ---------------- DATA UPDATE ----------------
  useEffect(() => {
    if (!seriesRef.current) return;

    if (!data || data.length === 0) {
      seriesRef.current.data.setAll([]);
      return;
    }

    seriesRef.current.data.setAll(data);

    // Re-apply active behavior after data load
    setTimeout(() => {
      const series = seriesRef.current;
      if (!series) return;
      series.slices.each((slice) => {
        const ctx = slice.dataItem?.dataContext as PieChartData;
        const isActive = !!activeCategory && ctx?.category === activeCategory;
        slice.set("scale", isActive ? 1.1 : 1);
      });
    });
  }, [data]);


  // ---------------- ACTIVE SLICE VISUAL UPDATE ----------------
  useEffect(() => {
    const series = seriesRef.current;
    if (!series) return;

    series.slices.each((slice) => {
      const ctx = slice.dataItem?.dataContext as PieChartData;
      const isActive = !!activeCategory && ctx?.category === activeCategory;

      // Using scale instead of 'active' is more stable in am5  
      slice.set("scale", isActive ? 1.1 : 1);
    });
  }, [activeCategory]);


  // ---------------- SIZE ----------------
  const finalWidth = width || defaultSize.width;
  const finalHeight = height || defaultSize.height;

  const handleLegendClick = (category: string) => {
    if (!onSliceClick) return;

    if (activeCategory === category) onSliceClick(null);
    else onSliceClick(category);
  };

  return (
    <div className={`pie-chart-container ${className}`}>
      <div className="pie-chart-header">
        <h3 className="pie-chart-title">{title}</h3>
      </div>

      <div className="pie-chart-content">
        <div
          ref={chartRef}
          style={{ width: "100%", height: `${finalHeight}px` }}
          className="pie-chart"
        />
      </div>

      {/* Legend */}
      <div
        className="pie-chart-custom-legend"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          width: "100%",
          padding: "8px 10px 10px",
          overflowX: "auto",
          overflowY: "hidden",
          whiteSpace: "nowrap",
          justifyContent: data.length <= 3 ? "center" : "flex-start"
        }}
      >
        {data.map((item) => {
          const isActive = activeCategory === item.category;
          return (
            <button
              key={item.category}
              type="button"
              className={`legend-pill ${isActive ? "legend-pill--active" : ""
                }`}
              onClick={() => handleLegendClick(item.category)}
            >
              <span
                className="legend-pill__color"
                style={{ backgroundColor: item.color || "#94a3b8" }}
              />
              <span className="legend-pill__label">{item.category}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};


export default React.memo(PieChart);

